import { useEffect, useRef, useState, useCallback } from 'react'
import { logger } from '@/lib/logger'
import type { MessageAttachment } from '@/types/conversations'

interface WebSocketMessage {
  type: string
  message_type?: string
  sender: string
  message: unknown
  timestamp?: string
  message_id?: string
  attachment?: MessageAttachment
  attachments?: MessageAttachment[]
  conversation_id?: string
}

interface UseRealtimeNotificationsOptions {
  conversationId?: string
  onNewMessage?: (conversationId: string, message: WebSocketMessage) => void
  onAttachmentUpdate?: (messageId: string, attachment: MessageAttachment) => void
  onConversationUpdate?: (conversationId: string, data: unknown) => void
}

interface UseRealtimeNotificationsReturn {
  notifications: WebSocketMessage[]
  websocket: WebSocket | null
  isConnected: boolean
  connectionError: string | null
}

export function useRealtimeNotifications({
  conversationId,
  onNewMessage,
  onAttachmentUpdate,
  onConversationUpdate
}: UseRealtimeNotificationsOptions = {}): UseRealtimeNotificationsReturn {
  const [notifications, setNotifications] = useState<WebSocketMessage[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const websocketRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 5

  const connect = useCallback(() => {
    if (!conversationId) return

    // Close existing connection
    if (websocketRef.current) {
      websocketRef.current.close()
    }

    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }

    try {
      // Build WebSocket URL - similar to the web frontend
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      const wsPath = `${protocol}//${host}/ws/chat/${conversationId}/?source=frontend-react`
      

      const ws = new WebSocket(wsPath)
      websocketRef.current = ws

      ws.onopen = () => {
        setIsConnected(true)
        setConnectionError(null)
        reconnectAttemptsRef.current = 0
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          

          // Handle different message types
          if (data.type === 'chat_message' || data.type === 'new_message') {
            const message: WebSocketMessage = {
              type: data.type,
              message_type: data.message_type,
              sender: data.sender,
              message: data.message,
              timestamp: data.timestamp || new Date().toISOString(),
              message_id: data.message_id,
              attachments: Array.isArray(data.attachments) ? data.attachments : [],
              conversation_id: conversationId
            }

            setNotifications(prev => [...prev, message])
            
            // Call the callback if provided
            if (onNewMessage) {
              onNewMessage(conversationId, message)
            }
          } else if (data.type === 'conversation_update') {
            // Handle conversation updates
            if (onConversationUpdate) {
              onConversationUpdate(conversationId, data)
            }
          } else if (data.type === 'attachment_update') {
            if (onAttachmentUpdate && data.message_id && data.attachment) {
              onAttachmentUpdate(String(data.message_id), data.attachment as MessageAttachment)
            }
          }
        } catch (err) {
          logger.error('Error parsing WebSocket message', {
            component: 'useRealtimeNotifications',
            action: 'ws_parse_error',
            conversationId
          }, { error: err })
        }
      }

      ws.onclose = (event) => {
        
        setIsConnected(false)
        
        // Attempt to reconnect if not a clean close
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000)
          reconnectAttemptsRef.current++
          
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect()
          }, delay)
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setConnectionError('Failed to reconnect after multiple attempts')
        }
      }

      ws.onerror = () => {
        logger.error('WebSocket error', {
          component: 'useRealtimeNotifications',
          action: 'ws_error',
          conversationId
        }, { error: 'WebSocket error' })
        
        setConnectionError('WebSocket connection error')
      }

    } catch (err) {
      logger.error('Failed to create WebSocket connection', {
        component: 'useRealtimeNotifications',
        action: 'ws_create_error',
        conversationId
      }, { error: err })
      
      setConnectionError('Failed to create WebSocket connection')
    }
  }, [conversationId, onNewMessage, onAttachmentUpdate, onConversationUpdate])

  const disconnect = useCallback(() => {
    if (websocketRef.current) {
      websocketRef.current.close(1000, 'component cleanup')
      websocketRef.current = null
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    
    setIsConnected(false)
    setConnectionError(null)
    reconnectAttemptsRef.current = 0
  }, [])

  // Connect when conversationId changes
  useEffect(() => {
    if (conversationId) {
      connect()
    } else {
      disconnect()
    }

    return () => {
      disconnect()
    }
  }, [conversationId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    notifications,
    websocket: websocketRef.current,
    isConnected,
    connectionError
  }
}
